import { useState, createElement } from 'react'
import { Play, Mail, FileText, TestTube, CheckCircle, XCircle, Clock, AlertTriangle, Download, Users } from 'lucide-react'
import CEDECReport from './CEDECReport'
import { generateRecommendations } from '../utils/recommendations'
import { themesToDomains, calculateThemeScores } from '../utils/themeUtils'
import { generatePdfBlobAndBase64 } from '../utils/pdfBlobHelper.js'
import questionnaireConfig from '../config/questionnaire.json'
import ConsultantProvisioningTool from './admin/ConsultantProvisioningTool'

/**
 * Admin Test Panel Component for CEDEC Assessment System
 * Secure testing utilities accessible only to authenticated admins
 */
const AdminTestPanel = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [testResults, setTestResults] = useState([])
  const [selectedScenario, setSelectedScenario] = useState('mixed')

  // Test scenarios available
  const scenarios = {
    excellent: { name: 'Entreprise Excellente', color: 'bg-green-100 text-green-800', icon: '🌟' },
    mixed: { name: 'Entreprise Mixte', color: 'bg-blue-100 text-blue-800', icon: '📊' },
    critical: { name: 'Entreprise en Difficulté', color: 'bg-red-100 text-red-800', icon: '🚨' }
  }

  // Add test result to the list
  const addTestResult = (result) => {
    setTestResults(prev => [
      {
        id: Date.now(),
        timestamp: new Date(),
        ...result
      },
      ...prev.slice(0, 9) // Keep last 10 results
    ])
  }

  // Get authorization token
  const getAuthToken = () => localStorage.getItem('adminToken')

  // Shared helper: Generate test data for any scenario (DRY principle)
  const generateTestData = (scenario) => {
    const questionnaire = questionnaireConfig.questionnaire
    const themes = questionnaire.themes
    const testScores = generateTestScores(themes, scenario)
    
    const assessmentData = {
      themes: themes,
      scores: testScores,
      totalQuestions: themes.reduce((sum, theme) => sum + theme.questions.length, 0),
      completedQuestions: Object.keys(testScores).length
    }
    
    const clientInfo = {
      firstName: 'Quentin',
      lastName: 'Berger',
      email: 'bergerq@cedec.fr',
      phone: '0123456789',
      company: 'CEDEC',
      consultantEmail: 'bergerq@cedec.fr',
      assessmentDate: new Date().toISOString() // Required by backend validation
    }
    
    const domains = themesToDomains(assessmentData.themes, 15)
    const themeScores = calculateThemeScores(assessmentData.themes, assessmentData.scores)
    const recommendations = generateRecommendations(themeScores, domains, clientInfo)
    
    return { questionnaire, assessmentData, clientInfo, recommendations }
  }

  // Test assessment email with REAL React-PDF (not backend text generator)
  const testAssessmentEmail = async (scenario) => {
    setIsLoading(true)
    
    try {
      console.log(`📧 Test email avec VRAI PDF React-PDF - Scénario: ${scenario}`)
      
      // Use shared helper to generate test data
      const { questionnaire, assessmentData, clientInfo, recommendations } = generateTestData(scenario)
      
      // Create PDF using React-PDF (PRODUCTION METHOD!)
      console.log('📄 Génération PDF React-PDF...')
      const pdfComponent = createElement(CEDECReport, {
        clientInfo,
        assessmentData,
        recommendations
      })
      
      const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
      console.log(`✅ PDF React-PDF généré: ${pdfResult.sizeKB}KB`)
      
      // Add validation fields only when sending to backend
      const scores = Object.values(assessmentData.scores).filter(s => s !== null && s !== undefined)
      const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0
      
      const assessmentDataForBackend = {
        ...assessmentData,
        questionnaire: questionnaire, // Backend validation requires full questionnaire
        averageScore: averageScore,
        completedAt: new Date().toISOString()
      }
      
      // Send using PRODUCTION endpoint with PDF blob
      const response = await fetch('/api/assessment/send-email-with-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ 
          clientInfo,
          assessmentData: assessmentDataForBackend,
          pdfBlob: pdfResult.base64
        })
      })

      const data = await response.json()
      
      console.log('✅ Réponse test email:', data)
      
      addTestResult({
        type: 'assessment-email',
        scenario,
        success: data.success,
        message: data.message,
        details: data.data,
        error: data.error
      })

      return data
    } catch (error) {
      console.error('❌ Erreur test email:', error)
      addTestResult({
        type: 'assessment-email',
        scenario,
        success: false,
        message: 'Erreur réseau',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Test simple email
  const testSimpleEmail = async () => {
    setIsLoading(true)
    
    try {
      const response = await fetch('/api/assessment/test/simple-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      const data = await response.json()
      
      addTestResult({
        type: 'simple-email',
        success: data.success,
        message: data.message,
        details: data.data,
        error: data.error
      })

      return data
    } catch (error) {
      addTestResult({
        type: 'simple-email',
        success: false,
        message: 'Erreur réseau',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Test PDF generation
  const testPDFGeneration = async (scenario) => {
    setIsLoading(true)
    
    try {
      const response = await fetch('/api/assessment/test/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ scenario })
      })

      const data = await response.json()
      
      addTestResult({
        type: 'pdf-generation',
        scenario,
        success: data.success,
        message: data.message,
        details: data.data,
        error: data.error
      })

      return data
    } catch (error) {
      addTestResult({
        type: 'pdf-generation',
        scenario,
        success: false,
        message: 'Erreur réseau',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Download test PDF using FRONTEND React-PDF (same as production)
  const downloadTestPDF = async (scenario) => {
    setIsLoading(true)
    
    try {
      console.log(`📄 Génération PDF frontend - Scénario: ${scenario}`)
      
      // Use shared helper to generate test data
      const { assessmentData, clientInfo, recommendations } = generateTestData(scenario)
      
      // Create PDF using React-PDF (SAME AS PRODUCTION!)
      const pdfComponent = createElement(CEDECReport, {
        clientInfo,
        assessmentData,
        recommendations
      })
      
      const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
      console.log(`✅ PDF créé: ${pdfResult.sizeKB}KB`)
      
      // Step 7: Download the PDF
      const url = window.URL.createObjectURL(pdfResult.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `test-rapport-cedec-${scenario}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        if (document.body.contains(a)) {
          document.body.removeChild(a)
        }
      }, 1000)

      addTestResult({
        type: 'pdf-download',
        scenario,
        success: true,
        message: `PDF ${scenario} téléchargé avec succès (Frontend React-PDF)`,
        details: {
          pdfSize: pdfResult.blob.size
        }
      })

      return { success: true }
    } catch (error) {
      console.error('❌ Erreur PDF frontend:', error)
      addTestResult({
        type: 'pdf-download',
        scenario,
        success: false,
        message: 'Erreur téléchargement PDF',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Save PDF to backend disk for manual inspection
  const savePdfToDisk = async (scenario) => {
    setIsLoading(true)
    
    try {
      console.log(`💾 Sauvegarde PDF sur disque - Scénario: ${scenario}`)
      
      // Use shared helper to generate test data
      const { assessmentData, clientInfo, recommendations } = generateTestData(scenario)
      
      const pdfComponent = createElement(CEDECReport, {
        clientInfo,
        assessmentData,
        recommendations
      })
      
      const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
      console.log(`✅ PDF généré: ${pdfResult.sizeKB}KB`)
      
      // Send to backend to save on disk
      const response = await fetch('/api/assessment/test/save-pdf-to-disk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ 
          pdfBlob: pdfResult.base64,
          scenario 
        })
      })

      const data = await response.json()

      addTestResult({
        type: 'pdf-save-disk',
        scenario,
        success: data.success,
        message: data.message,
        details: data.data,
        error: data.error
      })

      console.log('✅ PDFs sauvegardés sur le serveur:', data.data)

      return { success: true }
    } catch (error) {
      console.error('❌ Erreur sauvegarde PDF:', error)
      addTestResult({
        type: 'pdf-save-disk',
        scenario,
        success: false,
        message: 'Erreur sauvegarde PDF',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Test PDF blob round-trip (frontend → backend → download)
  const testPdfBlobRoundtrip = async (scenario) => {
    setIsLoading(true)
    
    try {
      console.log(`🔄 Test round-trip PDF blob - Scénario: ${scenario}`)
      console.log('📄 ÉTAPE 1: Génération PDF frontend (React-PDF)...')
      
      // Use shared helper to generate test data
      const { assessmentData, clientInfo, recommendations } = generateTestData(scenario)
      
      // Create PDF using React-PDF
      const pdfComponent = createElement(CEDECReport, {
        clientInfo,
        assessmentData,
        recommendations
      })
      
      const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
      console.log(`✅ ÉTAPE 1 TERMINÉE: PDF créé (${pdfResult.sizeKB}KB)`)
      console.log('🔍 Base64 length:', pdfResult.base64.length)
      console.log('🔍 Base64 start:', pdfResult.base64.substring(0, 100))
      
      // Step 7: Send to backend for round-trip conversion test
      console.log('📤 ÉTAPE 2: Envoi au backend pour conversion...')
      const response = await fetch('/api/assessment/test/pdf-blob-roundtrip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ 
          pdfBlob: pdfResult.base64,
          scenario 
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || `HTTP ${response.status}`)
      }

      // Step 8: Download the returned PDF
      console.log('📥 ÉTAPE 3: Réception et téléchargement du PDF converti...')
      const returnedBlob = await response.blob()
      console.log('✅ PDF reçu du backend:', returnedBlob.size, 'bytes')
      
      // Download both PDFs for comparison
      // Original PDF
      const originalUrl = window.URL.createObjectURL(pdfResult.blob)
      const originalLink = document.createElement('a')
      originalLink.href = originalUrl
      originalLink.download = `ORIGINAL-${scenario}-${Date.now()}.pdf`
      originalLink.style.display = 'none'
      document.body.appendChild(originalLink)
      originalLink.click()
      
      // Wait a bit before second download
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Returned PDF from backend
      const returnedUrl = window.URL.createObjectURL(returnedBlob)
      const returnedLink = document.createElement('a')
      returnedLink.href = returnedUrl
      returnedLink.download = `BACKEND-ROUNDTRIP-${scenario}-${Date.now()}.pdf`
      returnedLink.style.display = 'none'
      document.body.appendChild(returnedLink)
      returnedLink.click()
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(originalUrl)
        window.URL.revokeObjectURL(returnedUrl)
        if (document.body.contains(originalLink)) document.body.removeChild(originalLink)
        if (document.body.contains(returnedLink)) document.body.removeChild(returnedLink)
      }, 1000)

      addTestResult({
        type: 'pdf-roundtrip',
        scenario,
        success: true,
        message: `Test round-trip réussi! 2 PDFs téléchargés pour comparaison`,
        details: {
          originalSize: pdfResult.blob.size,
          returnedSize: returnedBlob.size,
          sizeDifference: Math.abs(pdfResult.blob.size - returnedBlob.size),
          match: pdfResult.blob.size === returnedBlob.size
        }
      })

      console.log('✅ Test round-trip terminé avec succès!')
      console.log('📊 Tailles:', {
        original: pdfResult.blob.size,
        returned: returnedBlob.size,
        difference: Math.abs(pdfResult.blob.size - returnedBlob.size)
      })

      return { success: true }
    } catch (error) {
      console.error('❌ Erreur test round-trip:', error)
      addTestResult({
        type: 'pdf-roundtrip',
        scenario,
        success: false,
        message: 'Erreur lors du test round-trip',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }
  
  // Helper: Generate test scores based on scenario
  const generateTestScores = (themes, scenario) => {
    const scores = {}
    
    themes.forEach((theme, themeIndex) => {
      theme.questions.forEach((question, qIndex) => {
        if (scenario === 'excellent') {
          // High scores: 8-10
          scores[question.id] = 8 + Math.floor(Math.random() * 3)
        } else if (scenario === 'critical') {
          // Low scores: 1-3
          scores[question.id] = 1 + Math.floor(Math.random() * 3)
        } else {
          // Mixed scores: realistic variety
          const patterns = [
            [8, 7, 9], // Good
            [6, 4, 7], // Mixed
            [3, 2, 4], // Critical
            [9, 8, 10], // Excellent
            [5, 6, 5]  // Average
          ]
          const pattern = patterns[themeIndex % patterns.length]
          scores[question.id] = pattern[qIndex % pattern.length]
        }
      })
    })
    
    return scores
  }

  // Test all scenarios
  const testAllScenarios = async () => {
    setIsLoading(true)
    
    try {
      const response = await fetch('/api/assessment/test/all-scenarios', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      const data = await response.json()
      
      addTestResult({
        type: 'all-scenarios',
        success: data.success,
        message: data.message,
        details: data.data,
        error: data.error
      })

      return data
    } catch (error) {
      addTestResult({
        type: 'all-scenarios',
        success: false,
        message: 'Erreur réseau',
        error: { message: error.message }
      })
      
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Format timestamp
  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Get icon for test type
  const getTestIcon = (type) => {
    const icons = {
      'assessment-email': <Mail className="w-4 h-4" />,
      'simple-email': <Mail className="w-4 h-4" />,
      'pdf-generation': <FileText className="w-4 h-4" />,
      'pdf-download': <Download className="w-4 h-4" />,
      'pdf-roundtrip': <Download className="w-4 h-4" />,
      'pdf-save-disk': <FileText className="w-4 h-4" />,
      'all-scenarios': <TestTube className="w-4 h-4" />
    }
    return icons[type] || <TestTube className="w-4 h-4" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <TestTube className="w-7 h-7 text-blue-600" />
            Tests & Utilitaires
          </h1>
          <p className="text-gray-600 mt-1">
            Outils de test pour emails et évaluations (accès admin uniquement)
          </p>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Environnement de test sécurisé</h3>
            <p className="text-sm text-amber-700 mt-1">
              Ces utilitaires utilisent des données de test et envoient <strong>3 emails</strong> vers <strong>bergerq@cedec.fr</strong> (client, consulting, et consultant). 
              Aucune donnée client réelle n'est utilisée.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Test Controls */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Tests Disponibles
          </h2>

          {/* Scenario Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scénario d'évaluation
            </label>
            <select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            >
              {Object.entries(scenarios).map(([key, scenario]) => (
                <option key={key} value={key}>
                  {scenario.icon} {scenario.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Chaque scénario génère des scores et recommandations différents
            </p>
          </div>

          {/* Test Buttons */}
          <div className="space-y-3">
            
            {/* Simple Email Test */}
            <button
              onClick={testSimpleEmail}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Mail className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Test Email Simple</div>
                <div className="text-sm text-green-100">Vérifier la configuration SMTP</div>
              </div>
            </button>

            {/* Assessment Email Test */}
            <button
              onClick={() => testAssessmentEmail(selectedScenario)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Mail className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Test Email d'Évaluation</div>
                <div className="text-sm text-blue-100">Envoyer avec le scénario sélectionné</div>
              </div>
            </button>

            {/* PDF Test */}
            <button
              onClick={() => testPDFGeneration(selectedScenario)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileText className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Test Génération PDF</div>
                <div className="text-sm text-orange-100">Générer sans envoyer d'email</div>
              </div>
            </button>

            {/* Download Test PDF */}
            <button
              onClick={() => downloadTestPDF(selectedScenario)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Télécharger PDF Test</div>
                <div className="text-sm text-indigo-100">Télécharger directement le PDF avec le scénario sélectionné</div>
              </div>
            </button>

            {/* PDF Blob Round-trip Test */}
            <button
              onClick={() => testPdfBlobRoundtrip(selectedScenario)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">🔄 Test Round-Trip PDF Blob</div>
                <div className="text-sm text-teal-100">Teste frontend → backend → download (2 PDFs pour comparer)</div>
              </div>
            </button>

            {/* Save PDF to Disk (Debugging) */}
            <button
              onClick={() => savePdfToDisk(selectedScenario)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileText className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">💾 Sauvegarder PDF sur Disque</div>
                <div className="text-sm text-yellow-100">Sauvegarde 2 PDFs dans backend/ pour inspection manuelle</div>
              </div>
            </button>

            {/* All Scenarios Test */}
            <button
              onClick={testAllScenarios}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <TestTube className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Test Tous les Scénarios</div>
                <div className="text-sm text-purple-100">Envoyer les 3 scénarios d'un coup</div>
              </div>
            </button>

          </div>

          {/* Loading Indicator */}
          {isLoading && (
            <div className="mt-4 flex items-center gap-2 text-blue-600">
              <Clock className="w-4 h-4 animate-spin" />
              <span className="text-sm">Test en cours...</span>
            </div>
          )}
        </div>

        {/* Test Results */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Résultats des Tests
          </h2>

          {testResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TestTube className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun test effectué pour le moment</p>
              <p className="text-sm">Lancez un test pour voir les résultats ici</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.map((result) => (
                <div
                  key={result.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    result.success 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-red-400 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {result.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getTestIcon(result.type)}
                        <span className="font-medium text-gray-900">
                          {result.type === 'assessment-email' && 'Email d\'Évaluation'}
                          {result.type === 'simple-email' && 'Email Simple'}
                          {result.type === 'pdf-generation' && 'Génération PDF'}
                          {result.type === 'pdf-download' && 'Téléchargement PDF'}
                          {result.type === 'pdf-roundtrip' && '🔄 Round-Trip PDF Blob'}
                          {result.type === 'pdf-save-disk' && '💾 Sauvegarde PDF Disque'}
                          {result.type === 'all-scenarios' && 'Tous les Scénarios'}
                        </span>
                        {result.scenario && (
                          <span className={`px-2 py-1 rounded-full text-xs ${scenarios[result.scenario]?.color}`}>
                            {scenarios[result.scenario]?.icon} {scenarios[result.scenario]?.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {formatTime(result.timestamp)}
                        </span>
                      </div>
                      
                      <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                        {result.message}
                      </p>
                      
                      {result.error && (
                        <p className="text-xs text-red-600 mt-1">
                          {result.error.message}
                        </p>
                      )}
                      
                      {result.details && (
                        <div className="text-xs text-gray-600 mt-2">
                          {result.details.averageScore && (
                            <span>Score: {result.details.averageScore}/10 • </span>
                          )}
                          {result.details.emailsSent && (
                            <span>Emails: {result.details.emailsSent} • </span>
                          )}
                          {result.details.pdfAttachmentSize && (
                            <span>PDF attaché: {result.details.pdfAttachmentSize}KB • </span>
                          )}
                          {result.details.pdfSize && (
                            <span>PDF: {Math.round(result.details.pdfSize / 1024)}KB</span>
                          )}
                          {result.details.originalSize && (
                            <span>Original: {Math.round(result.details.originalSize / 1024)}KB • </span>
                          )}
                          {result.details.returnedSize && (
                            <span>Backend: {Math.round(result.details.returnedSize / 1024)}KB • </span>
                          )}
                          {result.details.sizeDifference !== undefined && (
                            <span className={result.details.match ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'}>
                              Diff: {result.details.sizeDifference} bytes {result.details.match ? '✓' : '⚠️'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📋 Informations sur les tests
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Test Data Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Données de test utilisées</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Client :</strong> Quentin Berger</p>
              <p><strong>Email :</strong> bergerq@cedec.fr</p>
              <p><strong>Entreprise :</strong> CEDEC</p>
              <p><strong>Poste :</strong> Chef de Projet Innovation</p>
              <p><strong>Consultant :</strong> bergerq@cedec.fr</p>
            </div>
          </div>
          
          {/* Scenarios Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Scénarios disponibles</h4>
            <div className="space-y-2">
              {Object.entries(scenarios).map(([key, scenario]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className="text-lg">{scenario.icon}</span>
                  <span className="font-medium">{scenario.name}</span>
                  <span className="text-gray-500">
                    {key === 'excellent' && '(Scores 8-10/10)'}
                    {key === 'mixed' && '(Scores variés 2-10/10)'}
                    {key === 'critical' && '(Scores 1-3/10)'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Consultant Provisioning Tool */}
      <ConsultantProvisioningTool />
    </div>
  )
}

export default AdminTestPanel
